import { request } from '../api';

export type V3NextAction = {
  enabled: boolean;
  disabled_reason: string;
  route: string;
  label: string;
};

export type V3Progress = {
  done: number;
  total: number;
  percent: number;
  label: string;
};

export type V3Card = {
  id: string;
  entity_type: string;
  requirement_id?: string | null;
  session_id?: string | null;
  project_id?: string | null;
  run_id?: string | null;
  delivery_id?: string | null;
  title: string;
  user_status: string;
  summary: string;
  blocking_reason: string;
  progress: V3Progress;
  next_action: V3NextAction;
  diagnostics_available: boolean;
  diagnostics: Record<string, unknown>;
  tags: string[];
  source?: string;
  updated_at?: string | null;
};

export type V3Collection = {
  source: string;
  items: V3Card[];
  counts: Record<string, number>;
  notes?: string[];
};

export type V3Home = {
  overview: Record<string, number>;
  overview_cards?: Array<{ label: string; value: number; route: string }>;
  sections: Record<string, V3Card[]>;
  main_actions: V3NextAction[];
};

export type V3ModelItem = {
  model_id: string;
  model_ref: string;
  provider: string;
  provider_name: string;
  model_name: string;
  capabilities: string[];
  execution_adapter: string | null;
  enabled: boolean;
  credential_status: string;
  connection_status: string;
  last_tested_at: string | null;
  failure_reason: string;
  user_status: string;
  next_action: V3NextAction;
  diagnostics_available: boolean;
  diagnostics: Record<string, unknown>;
};

export type V3Models = {
  source: string;
  items: V3ModelItem[];
  counts: Record<string, number>;
};

export type V3ResourceItem = {
  resource_id: string;
  name: string;
  resource_type: string;
  summary: string;
  enabled: boolean;
  connection_status: string;
  last_checked_at: string | null;
  updated_at: string | null;
  next_action: V3NextAction;
  diagnostics_available: boolean;
  diagnostics: Record<string, unknown>;
};

export type V3Resources = {
  source: string;
  items: V3ResourceItem[];
  counts: Record<string, number>;
};

export type V3ModelAssignment = {
  source: string;
  preset: string;
  mode: string;
  updated_at: string | null;
  items: Array<Record<string, unknown>>;
  counts: Record<string, number>;
};

export type V3SystemDiagnostics = {
  source: string;
  revision: string;
  image_tag: string;
  environment: string;
  counts: Record<string, number>;
  checks: Array<Record<string, unknown>>;
  links: Record<string, string>;
};

const qs = (params?: Record<string, string | undefined>): string => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) sp.set(key, value);
  });
  const text = sp.toString();
  return text ? `?${text}` : '';
};

export const v3Api = {
  home: (): Promise<V3Home> => request('/api/v3/home'),
  requirements: (params?: Record<string, string | undefined>): Promise<V3Collection> => request(`/api/v3/requirements${qs(params)}`),
  plans: (params?: Record<string, string | undefined>): Promise<V3Collection> => request(`/api/v3/plans${qs(params)}`),
  projects: (params?: Record<string, string | undefined>): Promise<V3Collection> => request(`/api/v3/projects${qs(params)}`),
  progress: (): Promise<V3Collection> => request('/api/v3/progress'),
  acceptance: (): Promise<V3Collection> => request('/api/v3/acceptance'),
  deliveries: (): Promise<V3Collection> => request('/api/v3/deliveries'),
  projectDetail: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/projects/${encodeURIComponent(projectId)}`),
  projectIssues: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/projects/${encodeURIComponent(projectId)}/issues`),
  projectRepairs: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/projects/${encodeURIComponent(projectId)}/repairs`),
  projectQualityContract: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/projects/${encodeURIComponent(projectId)}/quality-contract`),
  projectProgress: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/progress/${encodeURIComponent(projectId)}`),
  runProgress: (runId: string): Promise<Record<string, unknown>> => request(`/api/v3/progress/runs/${encodeURIComponent(runId)}`),
  acceptanceDetail: (kind: 'tasks' | 'stages' | 'projects', projectId: string): Promise<Record<string, unknown>> =>
    request(`/api/v3/acceptance/${kind}/${encodeURIComponent(projectId)}`),
  deliveryDetail: (projectId: string): Promise<Record<string, unknown>> => request(`/api/v3/deliveries/${encodeURIComponent(projectId)}`),
  requirementDetail: (sessionId: string): Promise<Record<string, unknown>> => request(`/api/v3/requirements/${encodeURIComponent(sessionId)}`),
  planDetail: (sessionId: string, section: 'overview' | 'outline' | 'tasks' | 'history'): Promise<Record<string, unknown>> =>
    request(`/api/v3/plans/${encodeURIComponent(sessionId)}/${section}`),
  models: (): Promise<V3Models> => request('/api/v3/models'),
  resources: (): Promise<V3Resources> => request('/api/v3/resources'),
  modelAssignment: (): Promise<V3ModelAssignment> => request('/api/v3/model-assignment'),
  system: (): Promise<V3SystemDiagnostics> => request('/api/v3/system'),
  startFinalDeliveryReview: (projectId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/review`, { method: 'POST', body: JSON.stringify({}) }),
  approveFinalDelivery: (projectId: string, deliveryMethod = 'no_deploy'): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/approve`, {
      method: 'POST',
      body: JSON.stringify({ delivery_method: deliveryMethod }),
    }),
  rejectFinalDelivery: (projectId: string, reason: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  submitDeliveryIssue: (projectId: string, payload: { title: string; description?: string; severity?: string; evidence?: string[] }): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/issues`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  submitManualEvidence: (projectId: string, payload: {
    version?: string;
    title: string;
    description?: string;
    result?: string;
    acceptance_items?: string[];
    operation_steps?: string[];
    screenshots?: string[];
    logs?: string[];
    artifact_paths?: string[];
    related_issue_ids?: string[];
    related_repair_task_ids?: string[];
    console_errors?: string[];
    tester?: string;
  }): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/manual-evidence`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  reviewManualEvidence: (projectId: string, evidenceId: string, payload: { decision: 'approve' | 'reject'; reason?: string; opinion?: string; reviewer?: string }): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/manual-evidence/${encodeURIComponent(evidenceId)}/review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  submitRepairResult: (projectId: string, taskId: string, payload: {
    summary: string;
    modified_files?: string[];
    git_diff_summary?: string[];
    artifacts?: string[];
    build_and_test_result?: string;
    log_summary?: string;
    failure_reason?: string;
    preview?: string;
    submitter?: string;
  }): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/repair-tasks/${encodeURIComponent(taskId)}/result`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  createDeliveryRepairTask: (projectId: string, payload: { issue_ids?: string[]; title?: string; note?: string }): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/final-delivery/repair-tasks`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  planDeliveryPackage: (projectId: string, method = 'no_deploy'): Promise<Record<string, unknown>> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/delivery-package/plan`, {
      method: 'POST',
      body: JSON.stringify({ method }),
    }),
  deleteSession: (sessionId: string): Promise<void> => request(`/api/v1/plan_ingestion/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
  archiveSession: (sessionId: string): Promise<unknown> =>
    request(`/api/v3/sessions/${encodeURIComponent(sessionId)}/archive`, { method: 'POST', body: JSON.stringify({ reason: 'V3 用户归档' }) }),
  trashSession: (sessionId: string): Promise<unknown> =>
    request(`/api/v3/sessions/${encodeURIComponent(sessionId)}/trash`, { method: 'POST', body: JSON.stringify({ reason: 'V3 用户移入回收站' }) }),
  restoreSession: (sessionId: string): Promise<unknown> =>
    request(`/api/v3/sessions/${encodeURIComponent(sessionId)}/restore`, { method: 'POST', body: JSON.stringify({}) }),
  archiveProject: (projectId: string): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${encodeURIComponent(projectId)}/archive`, { method: 'POST', body: JSON.stringify({ reason: 'V3 用户归档' }) }),
  trashProject: (projectId: string, forceStopTasks = false): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${encodeURIComponent(projectId)}/delete`, {
      method: 'POST',
      body: JSON.stringify({ reason: forceStopTasks ? 'V3 用户停止阻塞任务并移入回收站' : 'V3 用户移入回收站', force_stop_tasks: forceStopTasks }),
    }),
  restoreProject: (projectId: string): Promise<unknown> =>
    request(`/api/v1/lifecycle/projects/${encodeURIComponent(projectId)}/restore`, { method: 'POST', body: JSON.stringify({}) }),
  createRequirementDraft: (payload: { raw_requirement: string; planning_model_id?: string | null; clarify_model_id?: string | null; mock_mode?: boolean }): Promise<Record<string, unknown>> =>
    request('/api/v1/plan_ingestion/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getRequirementDraft: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/draft_plans/${encodeURIComponent(sessionId)}`),
  generateRequirementOutline: (sessionId: string, payload: { planning_model_id: string; change_request?: string }): Promise<Record<string, unknown>> =>
    request(`/api/v1/draft_plans/${encodeURIComponent(sessionId)}/generate-outline`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  confirmRequirementOutline: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/draft_plans/${encodeURIComponent(sessionId)}/confirm-outline`, { method: 'POST', body: JSON.stringify({}) }),
  generateRequirementTaskDrafts: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/sessions/${encodeURIComponent(sessionId)}/task-drafts/generate`, {
      method: 'POST',
      body: JSON.stringify({ force_replace_existing: false }),
    }),
  getRequirementTaskDrafts: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/sessions/${encodeURIComponent(sessionId)}/task-drafts`),
  confirmRequirementTaskDrafts: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/sessions/${encodeURIComponent(sessionId)}/task-drafts/confirm`, { method: 'POST', body: JSON.stringify({}) }),
  createProjectFromRequirement: (sessionId: string): Promise<Record<string, unknown>> =>
    request(`/api/v1/draft_plans/${encodeURIComponent(sessionId)}/confirm-execution-plan`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'V3 new requirement project creation' }),
    }),
  importRequirementOutline: (sessionId: string, outline: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request(`/api/v1/plan_ingestion/sessions/${encodeURIComponent(sessionId)}/import-outline`, {
      method: 'POST',
      body: JSON.stringify(outline),
    }),
};
